import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, HeartHandshake, Loader2, RotateCcw, Ticket } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import CarteBillet from "../components/CarteBillet";
import RetourApp from "../components/RetourApp";
import { garderPanier, lirePanier, oublierPanier } from "../lib/repriseBillets";

const Cadre = ({ children, testid }) => (
  <div data-testid={testid} className="pt-32 pb-24 relative overflow-hidden mesh-bg">
    <PageDecor />
    <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10 space-y-6">{children}</div>
  </div>
);

/* Retour de Payrexx : la commande est relue sur le serveur (qui la vérifie chez Payrexx)
   jusqu'à ce qu'elle soit payée, puis le billet s'affiche. En version de test sans
   Payrexx, un bouton simule le paiement.
   Paiement annulé ou refusé (Jen, 01.11.2026) : Payrexx revient ici avec « annule=1 » ou
   « echec=1 ». Le serveur relit le paiement chez Payrexx et, s'il n'est pas payé, annule la
   commande : les places se libèrent tout de suite. La page le dit (« rien n'a été débité ») et
   propose « Recommencer ma commande », panier déjà rempli. */
export default function BilletsMerci() {
  useSEO({ title: "Merci ! Tes billets — 1000 ARTS", description: "Tes billets pour les spectacles de juin 2027.", canonical: "/spectacles/billets/merci" });
  const [params] = useSearchParams();
  const id = params.get("commande") || "";
  const j = params.get("j") || "";
  const simule = params.get("simule") === "1";
  // Payrexx : paiement annulé (« annule ») ou refusé / échoué (« echec »).
  const abandon = params.has("annule") ? "annule" : params.has("echec") ? "echec" : "";
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [erreur, setErreur] = useState("");
  // Après 2 minutes sans confirmation, on arrête d'attendre et on dit quoi faire (01.11.2026 :
  // la page tournait sans fin pour un paiement abandonné).
  const [tropLong, setTropLong] = useState(false);
  const essais = useRef(0);

  const lire = useCallback(async () => {
    const chemin = `${process.env.REACT_APP_BACKEND_URL}/api/billetterie/commandes/${encodeURIComponent(id)}`;
    const r = abandon
      ? await fetch(`${chemin}/abandon?j=${encodeURIComponent(j)}&motif=${abandon}`, { method: "POST" })
      : await fetch(`${chemin}?j=${encodeURIComponent(j)}`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || "Commande introuvable.");
    setC(d);
    return d;
  }, [id, j, abandon]);

  // Le paiement peut mettre quelques secondes à arriver : on relit toutes les 3 s (2 min).
  useEffect(() => {
    let fini = false;
    let minuterie = null;
    const tour = async () => {
      try {
        const d = await lire();
        // Retour « annulé » que le serveur n'a pas pu vérifier chez Payrexx : on n'attend pas.
        if (!fini && d.statut === "en_attente" && abandon) setTropLong(true);
        else if (!fini && d.statut === "en_attente" && !simule) {
          if (essais.current++ < 40) minuterie = setTimeout(tour, 3000); else setTropLong(true);
        }
      } catch (e) { setErreur(e.message); }
    };
    tour();
    return () => { fini = true; clearTimeout(minuterie); };
  }, [lire, simule, abandon]);
  // Payée : le panier gardé pour « Recommencer » ne sert plus.
  useEffect(() => { if (c?.statut === "payee") oublierPanier(); }, [c?.statut]);

  // « Recommencer ma commande » : le panier gardé avant le paiement revient tout rempli. Venu
  // d'un autre navigateur, on le refait avec les billets de la commande.
  const recommencer = () => {
    const garde = lirePanier();
    if (c && (!garde || garde.commande !== c.id)) {
      const panier = {};
      ((c.billets || []).length ? c.billets : [c]).forEach((b) => {
        const plein = Math.max(0, (b.plein || 0) - (b.offerts || 0));
        if (plein + (b.enfant || 0) > 0) panier[b.representation_id] = { plein, enfant: b.enfant || 0, bebe: Math.min(b.bebe || 0, plein) };
      });
      garderPanier({ commande: c.id, panier, contact: { prenom: c.prenom || "", nom: c.nom || "" } });
    }
    navigate("/spectacles/billets?reprendre=1");
  };
  const simulerAnnulation = () => navigate(`/spectacles/billets/merci?commande=${encodeURIComponent(id)}&j=${encodeURIComponent(j)}&annule=1`, { replace: true });

  const simuler = async () => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billetterie/commandes/${encodeURIComponent(id)}/simuler-paiement?j=${encodeURIComponent(j)}`, { method: "POST" });
    const d = await r.json().catch(() => ({}));
    if (r.ok) setC(d); else setErreur(d.detail || "Simulation impossible.");
  };

  return (
    <Cadre testid="page-billets-merci">
      <Link to="/spectacles/billets" className="btn-ghost"><ArrowLeft size={16} /> Billetterie</Link>
      {erreur && <div className="card-flat text-center">{erreur}</div>}
      {!c && !erreur && <div className="py-16 grid place-items-center"><Loader2 className="animate-spin" /></div>}
      {c && c.statut === "payee" && (
        <>
          <div className="text-center">
            <h1 className="font-display text-4xl">Merci {c.prenom} !</h1>
            <p className="text-[var(--muted)] mt-2">
              Ta commande est {((c.billets || []).length ? c.billets : [c]).some((b) => b.montant > 0) ? "payée" : "confirmée"}{(c.billets || []).length > 1 ? ` : ${c.billets.length} représentations, un billet et un QR code pour chacune` : ""}.
              Tu reçois aussi tes billets par e-mail (PDF).
            </p>
          </div>
          {/* Un panier de plusieurs représentations : un billet (son QR code) par représentation. */}
          {((c.billets || []).length ? c.billets : [c]).map((b) => <CarteBillet key={b.id} billet={b} />)}
          {/* Les créneaux de bénévole pris dans la même commande (Jen, 30.10.2026). Le bénévole
              peut être quelqu'un d'autre (la grand-mère…) : on le nomme (01.11.2026). */}
          {(c.benevoles || []).length > 0 && (() => {
            const autre = c.benevoles.find((b) => b.benevole)?.benevole;
            return (
              <div className="rounded-3xl p-5" style={{ background: "#FDF2F8", border: "1px solid #FBCFE8" }} data-testid="billets-merci-benevoles">
                <div className="font-semibold flex items-center gap-2" style={{ color: "#9D174D" }}><HeartHandshake size={16} />
                  {autre ? `Merci pour ton aide : ${autre} sera bénévole !` : "Merci d'être bénévole !"}</div>
                <ul className="mt-2 text-sm list-disc pl-5">
                  {c.benevoles.map((b) => <li key={`${b.quand}-${b.role}`}><strong>{b.role}</strong> · {b.titre}, {b.quand}</li>)}
                </ul>
                {/* Confirmé d'office (Jen, 01.11.2026) : l'e-mail part tout de suite. */}
                <p className="text-sm mt-2">C'est confirmé : {autre ? "l'e-mail de confirmation" : "tu reçois un e-mail"} avec le lien du groupe WhatsApp des bénévoles et un lien pour changer ou annuler {c.benevoles.length > 1 ? (autre ? "ses créneaux" : "tes créneaux") : (autre ? "son créneau" : "ton créneau")}.</p>
              </div>
            );
          })()}
        </>
      )}
      {c && c.statut === "en_attente" && (
        <div className="card-flat text-center space-y-3" data-testid="billets-attente">
          <Ticket className="mx-auto" />
          {simule ? (
            <>
              <p>Version de test : le paiement est simulé, rien n'est débité.</p>
              <div className="flex flex-wrap justify-center gap-2">
                <button className="btn-primary" onClick={simuler} data-testid="billets-simuler">Simuler le paiement TWINT</button>
                {/* Le chemin « paiement annulé » à essayer aussi (places libérées, panier repris). */}
                <button className="btn-ghost" onClick={simulerAnnulation} data-testid="billets-simuler-annulation">Simuler un paiement annulé</button>
              </div>
            </>
          ) : tropLong ? (
            <div data-testid="billets-attente-trop-long">
              <p>Le paiement n'est pas confirmé.</p>
              <p className="text-sm text-[var(--muted)] mt-1">Si tu as payé, tes billets arrivent par e-mail dans quelques minutes. Sinon, rien n'a été débité : tu peux recommencer ta commande.</p>
              <button type="button" onClick={recommencer} className="btn-primary mt-4 inline-flex" data-testid="billets-recommencer"><RotateCcw size={16} /> Recommencer ma commande</button>
            </div>
          ) : (
            <p className="flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> On attend la confirmation du paiement…</p>
          )}
        </div>
      )}
      {c && c.statut === "annulee" && (c.paye_apres_annulation ? (
        /* Payée APRÈS l'annulation, sans place pour revenir : l'école est prévenue. */
        <div className="card-flat text-center space-y-3" data-testid="billets-paye-apres-annulation">
          <Ticket className="mx-auto" />
          {c.a_regler ? (
            <>
              <h1 className="font-display text-3xl">Ton paiement est bien arrivé</h1>
              <p className="text-[var(--muted)]">Mais entre-temps, les places de cette commande ont été reprises : la représentation est complète.
                L'école est prévenue et te contacte très vite pour te proposer une autre date ou te rembourser.</p>
              <p className="text-sm">Une question ? info1000arts@gmail.com · 079 950 40 31</p>
            </>
          ) : <p>Cette commande a été annulée et ton paiement remboursé.</p>}
        </div>
      ) : c.annulation === "paiement" ? (
        /* Paiement annulé ou refusé chez Payrexx (01.11.2026) : rien n'est débité, les places
           sont libérées tout de suite ; on recommence d'un geste, panier rempli. */
        <div className="card-flat !px-5 sm:!px-7 text-center space-y-4 py-10" data-testid="billets-paiement-annule">
          <span className="w-14 h-14 rounded-full grid place-items-center mx-auto" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}><Ticket size={26} /></span>
          <div>
            <h1 className="font-display text-3xl md:text-4xl">{abandon === "echec" ? "Le paiement n'a pas abouti" : "Paiement annulé"}</h1>
            <p className="text-lg mt-2" data-testid="billets-rien-debite">Rien n'a été débité.</p>
          </div>
          <p className="text-[var(--muted)] max-w-md mx-auto">Les places que tu avais choisies sont de nouveau libres. Tu peux recommencer quand tu veux : ta commande est déjà remplie, il ne reste qu'à payer.</p>
          <button type="button" onClick={recommencer} className="btn-primary inline-flex whitespace-nowrap !px-6" data-testid="billets-recommencer"><RotateCcw size={16} /> Recommencer ma commande</button>
        </div>
      ) : <div className="card-flat text-center">Cette commande a été annulée.</div>)}
    </Cadre>
  );
}

/* /billet/<jeton> : le billet, tel qu'il est dans l'e-mail (lien du QR code). */
export function Billet() {
  useSEO({ title: "Ton billet — 1000 ARTS", description: "Ton billet pour les spectacles de juin 2027.", canonical: "/billet" });
  const { jeton } = useParams();
  const [b, setB] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billetterie/billets/${encodeURIComponent(jeton)}`)
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || "Billet introuvable."); setB(d); })
      .catch((e) => setErreur(e.message));
  }, [jeton]);
  return (
    <Cadre testid="page-billet">
      {/* Ouvert depuis l'app (app=1) : « ← Retour à l'app » (Jen, 01.11.2026). */}
      <RetourApp />
      {erreur && <div className="card-flat text-center">{erreur}</div>}
      {!b && !erreur && <div className="py-16 grid place-items-center"><Loader2 className="animate-spin" /></div>}
      {b && <CarteBillet billet={b} />}
    </Cadre>
  );
}
